import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useIstDay } from "../lib/dates";
import { useBranch } from "../context/BranchContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/shared";
import { Button } from "../components/ui/button";
import { BusinessWin, MonthCompareWin, ExpenseWin, TopStaffWin } from "../components/dashboard/RowOne";
import { UpcomingWin, PaymentModesWin, BalanceWin, WeeklyOffWin } from "../components/dashboard/RowTwo";
import { AdvisorCard } from "../components/dashboard/AdvisorCard";

const PERIODS = [
  { k: "today", l: "Today" }, { k: "yesterday", l: "Yesterday" },
  { k: "week", l: "This Week" }, { k: "month", l: "This Month" },
];

const prettyRange = (d) => {
  const f = (s) => { const [y, m, dd] = s.split("-"); return `${dd}/${m}/${y}`; };
  return d.start === d.end ? f(d.start) : `${f(d.start)} – ${f(d.end)}`;
};

export default function Dashboard() {
  const { branchParam, selected, branches } = useBranch();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [period, setPeriod] = useState("today");
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);
  const isAdmin = ["OWNER", "SUPER_ADMIN"].includes(user?.role);
  const branchObj = branches.find((b) => b.id === selected);
  const shortBranch = branchObj ? branchObj.name.split("–").pop().trim() : "";
  const isAllBranches = isAdmin && (!selected || selected === "all");
  const dashTitle = isAllBranches ? "Owner Dashboard" : `${shortBranch || "Branch"} Dashboard`;
  const dashSubtitle = isAllBranches ? "Consolidated performance across DEFINE SALONS"
    : (isAdmin ? `Performance for ${shortBranch}` : "Your branch performance");

  const istDay = useIstDay();
  useEffect(() => {
    setDenied(false);
    api.get("/reports/owner-dashboard", { params: { period, branch_id: branchParam } })
      .then((r) => setData(r.data))
      .catch((e) => { if (e.response?.status === 403) setDenied(true); });
  }, [period, branchParam, istDay]);

  return (
    <div>
      <PageHeader title={dashTitle} subtitle={data ? `${dashSubtitle} · ${prettyRange(data)}` : dashSubtitle}
        actions={
          <div className="flex gap-1 bg-secondary rounded-sm p-1" data-testid="dashboard-filters">
            {PERIODS.map((p) => (
              <Button key={p.k} size="sm" variant="ghost"
                data-testid={`period-${p.k}`}
                onClick={() => setPeriod(p.k)}
                className={`h-8 rounded-sm ${period === p.k ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
                {p.l}
              </Button>
            ))}
          </div>
        } />

      {denied ? (
        <div className="mt-6 rounded-sm border border-border bg-card p-8 text-center" data-testid="dashboard-empty">
          <p className="text-lg text-foreground">Welcome{user?.name ? `, ${user.name}` : ""} 👋</p>
          <p className="text-muted-foreground mt-1">Use the menu to manage Appointments, Billing and Customers for your branch. Business metrics are visible to management.</p>
        </div>
      ) : !data ? <p className="text-muted-foreground">Loading…</p> : (
        <div className="space-y-4" data-testid="owner-dashboard">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="dashboard-row-1">
            <BusinessWin d={data} />
            <MonthCompareWin d={data} />
            <ExpenseWin d={data} />
            <TopStaffWin d={data} onStaff={(s) => navigate(`/incentives?staff=${s.staff_id}`)} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="dashboard-row-2">
            <UpcomingWin d={data} />
            <PaymentModesWin d={data} />
            <BalanceWin d={data} />
            <WeeklyOffWin d={data} />
          </div>
          <AdvisorCard period={period} branchParam={branchParam} />
        </div>
      )}
    </div>
  );
}
